import { Injectable } from 'framework/di';
import { pipe } from 'framework/functional';

import { BoostAPI } from '../app/app.api';
import { AppService } from '../app/app.service';
import { B2BService } from '../b2b/b2b.service';
import { PlatformLoader } from '../platform/platform.loader';
import { FilterHelper } from './filter.helper';
import {
  type FilterAPIAdditionParams,
  type FilterAPIParams,
  type FilterAPIResponse,
  type FilterOptionsProps,
} from './filter.model';
import { FilterStore } from './filter.store';

/**
 * API service for handling filter widget requests to the Boost filter engine.
 *
 * This service provides functionality for fetching filtered product results from
 * the Boost API. It manages request construction, caching, and response processing
 * through extensible methods that allow customization for shop-specific requirements.
 *
 * @remarks
 * Key features:
 * - Automatic caching of filter responses
 * - Query parameter building with locale and B2B support
 * - Flexible response type handling (JSON/HTML)
 * - Support for pagination and filtering
 * - Extensible parameter and response processing
 *
 * @example
 * Basic usage:
 * ```typescript
 * const api = new FilterAPI(store, helper, appService);
 * const response = await api.fetchAPI(
 *   '/collections/all',
 *   'product-list',
 *   {
 *     additionParams: { page: 1, limit: 20 },
 *     responseType: 'json'
 *   }
 * );
 * ```
 */
@Injectable
export class FilterAPI extends BoostAPI {
  /**
   * Creates a new FilterAPI instance with injected dependencies
   *
   * @param store - Filter store for accessing filter state and cache
   * @param helper - Helper class for utility functions
   * @param appService - Application service for accessing TAE app configuration
   */
  constructor(
    protected store: FilterStore,
    protected helper: FilterHelper,
    protected platformLoader: PlatformLoader,
    protected appService: AppService,
    protected b2bService: B2BService
  ) {
    super(platformLoader);
    this.config.baseURL = this.appService.TAEAppConfig.filterUrl;
  }

  /**
   * Main method to fetch filter API data with caching support
   *
   * @param url - Base URL for the filter API
   * @param domId - DOM element ID for rendering results
   * @param addition - Additional parameters for customizing the request
   * @returns Promise resolving to the API response (HTML or JSON)
   *
   * @example
   * ```typescript
   * const result = await api.fetchAPI(
   *   '/collections/summer',
   *   'product-grid',
   *   {
   *     responseType: 'json',
   *     additionParams: { page: 2 }
   *   }
   * );
   * ```
   */
  async fetchAPI(
    url: string,
    addition: FilterAPIAdditionParams = {}
  ): Promise<string | FilterAPIResponse | null> {
    try {
      // Update loading state
      this.store.setState({ isLoading: true, error: null });

      // Determine if search page
      const isSearch = this.helper.isSearchPage();
      const urlParams = new URLSearchParams(window.location.search);
      const searchQuery = urlParams.get('q');
      const finalUrl = isSearch || searchQuery ? this.appService.TAEAppConfig.searchUrl : url;

      // Build parameters
      const { responseType = 'json', modifyParams, additionParams = {} } = addition;

      const params = this.buildParams(additionParams, modifyParams);

      // Build query parameters
      const queryParams = this.helper.buildQueryParams(params);

      // Generate cache key
      const cacheKey = this.helper.generateCacheKey(queryParams);

      // Check cache
      const cachedData = this.store.getCacheEntry(cacheKey);
      this.store.setState({ latestRequestKey: cacheKey });

      let html: string | null = null;
      let filter: FilterOptionsProps = {} as FilterOptionsProps;
      let bundles: unknown[] = [];
      let meta: any = null;
      if (cachedData) {
        // Use cached data
        html = cachedData.html;
        filter = cachedData.filter;
        bundles = cachedData.bundles;

        this.store.setState({ isLoading: false, dynamicBundles: bundles, filter: filter });
      } else {
        // Make API call
        queryParams.set('t', String(Date.now()));
        queryParams.set('sid', this.helper.getSessionId());

        // Apply filter settings
        this.applyFilterSettings(queryParams);

        // Add custom parameters if available
        this.addCustomParams(queryParams);

        // Fetch from API
        const response = await this.makeAPIRequest(finalUrl, queryParams, responseType);

        if (response) {
          html = response.html;
          filter = response.filter;
          bundles = response.bundles || [];
          meta = response.meta || null;

          // Cache the response
          this.store.setCacheEntry(cacheKey, { html, filter, bundles });

          const optionsMap = filter.options
            ?.filter((f) => f.filterOptionId)
            .reduce((obj, val) => ({ ...obj, [`${val.filterOptionId}`]: val }), {});

          // Update store with filter data
          this.store.setState({
            isLoading: false,
            filter,
            optionsMap,
            dynamicBundles: bundles,
          });
        }
      }

      // Process and return result
      if (html && this.store.getState().latestRequestKey === cacheKey) {
        const processedHtml = this.helper.processHTMLResponse(html);

        if (responseType === 'json') {
          return {
            html: processedHtml,
            filter,
            bundles,
            meta,
          };
        }

        return processedHtml;
      }

      return null;
    } catch (error) {
      this.store.setState({
        isLoading: false,
        error: error instanceof Error ? error : new Error('Unknown error'),
      });
      throw error;
    }
  }

  /**
   * Builds parameters for the API request from store state and additional params.
   * Uses a functional pipeline approach to transform parameters through multiple stages.
   *
   * This method orchestrates the entire parameter building process by applying transformations
   * for sorting, pagination, device detection, customer data, filters, and shop-specific settings.
   * Override this method to customize the parameter building pipeline for shop-specific requirements.
   *
   * @param additionParams - Additional parameters to merge into the final params object
   * @param modifyParams - Optional function to modify final parameters before returning
   * @returns Built parameters object ready for API request
   *
   * @example
   * Extend to add custom shop-specific parameters:
   * ```javascript
   * window.boostWidgetIntegration.extend('FilterAPI', (FilterAPI) => {
   *   return class CustomFilterAPI extends FilterAPI {
   *     protected buildParams(additionParams, modifyParams) {
   *       const params = super.buildParams(additionParams, modifyParams);
   *       // Add shop-specific discount parameters
   *       if (this.customerHasVIPStatus()) {
   *         params.vip_pricing = true;
   *         params.discount_tier = 'premium';
   *       }
   *       return params;
   *     }
   *   };
   * });
   * ```
   */
  protected buildParams(
    additionParams: Record<string, unknown>,
    modifyParams?: ((params: FilterAPIParams) => FilterAPIParams) | null
  ): FilterAPIParams {
    const filterState = this.store.getState();
    const selectedFilter = this.store.getState('selectedFilter') || [];

    const filteredParams = this.helper.parseFilterTreeToParams(selectedFilter || []);

    // Build base parameters
    const baseParams: FilterAPIParams = {
      ...(filterState.defaultParams as unknown as FilterAPIParams),
      ...filteredParams,
    };

    // Use pipe to apply transformations (split into two pipes due to type limitation)
    const intermediateParams = pipe(
      baseParams,
      this.addSortParams.bind(this),
      this.addPaginationParams.bind(this),
      this.addDeviceParams.bind(this),
      this.addCustomerParams.bind(this),
      (params: FilterAPIParams) => this.mergeAdditionalParams(params, additionParams),
      this.addAndConditionFlags.bind(this),
      this.addShowFilterTreeParam.bind(this)
    );

    const transformedParams = pipe(
      intermediateParams,
      this.addCollectionScope.bind(this),
      this.handleTagParams.bind(this),
      (p) => this.addFilterFlag(p, filteredParams),
      this.handleVendorPage.bind(this),
      this.handleBannerParams.bind(this),
      this.helper.addLocaleParam.bind(this.helper),
      (p) => this.b2bService.addB2BRequestParams(p)
    );

    // Apply custom modifications if provided
    return modifyParams
      ? modifyParams(transformedParams as FilterAPIParams)
      : (transformedParams as FilterAPIParams);
  }

  /**
   * Adds sorting parameters from filter state to API request params.
   *
   * Extracts the sort value from FilterStore state and includes it in the API request.
   * Override this method to customize sorting behavior, add custom sort options, or
   * implement shop-specific sorting rules.
   *
   * @param params - Current filter parameters being built
   * @returns Enhanced parameters with sort value added if present in state
   *
   * @remarks
   * Sort value is only added if it exists in the filter state. If no sort is set,
   * the API will use its default sorting behavior.
   *
   * @example
   * Override to add default sorting:
   * ```javascript
   * window.boostWidgetIntegration.extend('FilterAPI', (FilterAPI) => {
   *   return class CustomFilterAPI extends FilterAPI {
   *     protected addSortParams(params) {
   *       const enhanced = super.addSortParams(params);
   *       // Add default sort if none specified
   *       if (!enhanced.sort) {
   *         enhanced.sort = 'best-selling';
   *       }
   *       return enhanced;
   *     }
   *   };
   * });
   * ```
   */
  protected addSortParams(params: FilterAPIParams): FilterAPIParams {
    const filterState = this.store.getState();
    const sortState = (filterState as unknown as { sort?: { sort: string; isShow: boolean } }).sort;
    const sortValue = sortState?.sort;
    if (sortValue) {
      return { ...params, sort: sortValue };
    }
    return params;
  }

  /**
   * Adds pagination parameters from filter state to API request params.
   *
   * Extracts the current page number from FilterStore pagination state and includes
   * it in the API request. Override to customize pagination behavior or implement
   * infinite scroll patterns.
   *
   * @param params - Current filter parameters being built
   * @returns Enhanced parameters with page number added if present in state
   *
   * @remarks
   * Page parameter is only added if pagination exists in filter state. Default page
   * is 1 if not specified.
   *
   * @example
   * Override to implement infinite scroll:
   * ```javascript
   * window.boostWidgetIntegration.extend('FilterAPI', (FilterAPI) => {
   *   return class CustomFilterAPI extends FilterAPI {
   *     protected addPaginationParams(params) {
   *       const enhanced = super.addPaginationParams(params);
   *       // For infinite scroll, always increment page
   *       if (this.isInfiniteScrollMode()) {
   *         enhanced.page = this.getNextPageForInfiniteScroll();
   *       }
   *       return enhanced;
   *     }
   *   };
   * });
   * ```
   */
  protected addPaginationParams(params: FilterAPIParams): FilterAPIParams {
    const filterState = this.store.getState();

    const paginationValue = (filterState as unknown as { pagination?: any }).pagination;

    if (paginationValue) {
      return { ...params, page: paginationValue?.page };
    }
    return params;
  }

  /**
   * Adds device type and viewport parameters to API request.
   *
   * Detects the current device type (mobile/tablet) based on viewport width and
   * includes this information in the API request. The API uses this to return
   * device-appropriate responses. Override to customize device detection logic
   * or add additional device-specific parameters.
   *
   * @param params - Current filter parameters being built
   * @returns Enhanced parameters with isMobile and isTabletPortraitMax flags
   *
   * @remarks
   * Device Detection:
   * - Reads breakpoint from template settings (default: 768px for tablet portrait max)
   * - Uses FilterHelper.isMobile() for detection
   * - Both flags use the same breakpoint check currently
   *
   * @example
   * Override to add custom device parameters:
   * ```javascript
   * window.boostWidgetIntegration.extend('FilterAPI', (FilterAPI) => {
   *   return class CustomFilterAPI extends FilterAPI {
   *     protected addDeviceParams(params) {
   *       const enhanced = super.addDeviceParams(params);
   *       // Add retina display detection for high-res images
   *       enhanced.isRetina = window.devicePixelRatio > 1;
   *       // Add custom mobile breakpoint for specific shops
   *       enhanced.isSmallMobile = window.innerWidth < 375;
   *       return enhanced;
   *     }
   *   };
   * });
   * ```
   */
  protected addDeviceParams(params: FilterAPIParams): FilterAPIParams {
    const templateSettings = this.appService.templateSettings as {
      generalSettings?: { breakpointtabletportraitmax?: string };
    };
    const breakpoint = parseInt(
      templateSettings.generalSettings?.breakpointtabletportraitmax || '768',
      10
    );
    const breakpointMobile = parseInt(
      templateSettings.generalSettings?.breakpointtabletportraitmax || '575',
      10
    );

    return {
      ...params,
      isMobile: this.helper.isMobile(breakpointMobile),
      isTabletPortraitMax: this.helper.isMobile(breakpoint),
    };
  }

  /**
   * Adds customer identification and session tracking parameters to API request.
   *
   * Includes customer ID (if logged in) and marks the request as not a first load.
   * The API uses customer ID for personalization. Override to add custom customer
   * attributes or implement shop-specific customer tracking.
   *
   * @param params - Current filter parameters being built
   * @returns Enhanced parameters with first_load flag and customer_id if available
   *
   * @remarks
   * Customer Tracking:
   * - first_load is always false (initial load handled separately)
   * - customer_id is undefined for guest users
   * - Customer ID is used for personalized recommendations and pricing
   *
   * @example
   * Override to add customer segment information:
   * ```javascript
   * window.boostWidgetIntegration.extend('FilterAPI', (FilterAPI) => {
   *   return class CustomFilterAPI extends FilterAPI {
   *     protected addCustomerParams(params) {
   *       const enhanced = super.addCustomerParams(params);
   *       // Add customer segment for targeted filtering
   *       const customer = this.getCustomerData();
   *       if (customer) {
   *         enhanced.customer_segment = customer.tags?.includes('vip') ? 'vip' : 'standard';
   *         enhanced.customer_lifetime_value = customer.totalSpent || 0;
   *       }
   *       return enhanced;
   *     }
   *   };
   * });
   * ```
   */
  protected addCustomerParams(params: FilterAPIParams): FilterAPIParams {
    return {
      ...params,
      first_load: false,
      customer_id: this.helper.getCustomerId() || undefined,
    };
  }

  /**
   * Merges additional parameters provided by caller into the parameter object.
   *
   * Combines any extra parameters passed to fetchAPI() with the base parameters
   * built from filter state. Additional params take precedence over base params.
   * Override to validate, transform, or filter additional parameters.
   *
   * @param params - Current filter parameters being built
   * @param additionParams - Additional parameters to merge (from fetchAPI call)
   * @returns Combined parameters with additional params merged in
   *
   * @remarks
   * Merge Behavior:
   * - Additional params override base params if keys conflict
   * - Empty object is handled gracefully (no-op)
   * - Type safety maintained through FilterAPIParams interface
   *
   * @example
   * Override to validate additional parameters:
   * ```javascript
   * window.boostWidgetIntegration.extend('FilterAPI', (FilterAPI) => {
   *   return class CustomFilterAPI extends FilterAPI {
   *     protected mergeAdditionalParams(params, additionParams) {
   *       // Filter out unauthorized parameters
   *       const allowedParams = this.filterAllowedParams(additionParams);
   *       return super.mergeAdditionalParams(params, allowedParams);
   *     }
   *
   *     filterAllowedParams(params) {
   *       const allowed = ['page', 'limit', 'sort'];
   *       return Object.keys(params)
   *         .filter(key => allowed.includes(key))
   *         .reduce((obj, key) => ({ ...obj, [key]: params[key] }), {});
   *     }
   *   };
   * });
   * ```
   */
  protected mergeAdditionalParams(
    params: FilterAPIParams,
    additionParams: Record<string, unknown>
  ): FilterAPIParams {
    if (additionParams && Object.keys(additionParams).length > 0) {
      return { ...params, ...(additionParams as FilterAPIParams) };
    }
    return params;
  }

  /**
   * Adds AND condition flags for filter options that require it.
   *
   * Checks filter options in state for `useAndCondition` flag and adds corresponding
   * `_and_condition` parameters. This allows multiple filter values within the same
   * option to use AND logic instead of OR logic (e.g., products must have BOTH tags).
   * Override to customize AND condition logic or add custom boolean operators.
   *
   * @param params - Current filter parameters being built
   * @returns Enhanced parameters with AND condition flags for applicable filter options
   *
   * @remarks
   * AND Condition Behavior:
   * - Only adds flags for options with `useAndCondition: true` in filter state
   * - Flag format: `{filterOptionId}_and_condition: true`
   * - API interprets these flags to apply AND logic instead of OR
   * - Default behavior (without flag) is OR logic
   *
   * @example
   * Override to add custom boolean operators:
   * ```javascript
   * window.boostWidgetIntegration.extend('FilterAPI', (FilterAPI) => {
   *   return class CustomFilterAPI extends FilterAPI {
   *     protected addAndConditionFlags(params) {
   *       const enhanced = super.addAndConditionFlags(params);
   *       // Add NOT condition support for exclusion filters
   *       const options = this.store.getState().filter?.options || [];
   *       options.forEach(option => {
   *         if (option.useNotCondition) {
   *           enhanced[`${option.filterOptionId}_not_condition`] = true;
   *         }
   *       });
   *       return enhanced;
   *     }
   *   };
   * });
   * ```
   */
  protected addAndConditionFlags(params: FilterAPIParams): FilterAPIParams {
    const filterState = this.store.getState();
    const options = filterState.filter?.options || [];

    const andConditionParams: Record<string, boolean> = {};
    options.forEach((option: unknown) => {
      if (
        typeof option === 'object' &&
        option !== null &&
        'useAndCondition' in option &&
        'filterOptionId' in option
      ) {
        const typedOption = option as { useAndCondition?: boolean; filterOptionId?: string };
        if (typedOption.useAndCondition && typedOption.filterOptionId) {
          andConditionParams[`${typedOption.filterOptionId}_and_condition`] = true;
        }
      }
    });

    return { ...params, ...andConditionParams };
  }

  /**
   * Adds showFilterTree parameter based on filter toggle button state.
   *
   * Checks the filter toggle button state in FilterStore and includes showFilterTree
   * flag if the filter tree should be displayed. This is used for mobile/off-canvas
   * filter layouts. Override to customize filter tree visibility logic.
   *
   * @param params - Current filter parameters being built
   * @returns Enhanced parameters with showFilterTree flag if toggle button state is active
   *
   * @remarks
   * Filter Tree Visibility:
   * - Only adds flag if filter-toggle-button state exists and showFilterTree is true
   * - Used primarily for mobile and off-canvas filter layouts
   * - API uses this to return appropriate filter tree data
   *
   * @example
   * Override to force filter tree display:
   * ```javascript
   * window.boostWidgetIntegration.extend('FilterAPI', (FilterAPI) => {
   *   return class CustomFilterAPI extends FilterAPI {
   *     protected addShowFilterTreeParam(params) {
   *       const enhanced = super.addShowFilterTreeParam(params);
   *       // Always show filter tree for mobile users
   *       if (this.helper.isMobile()) {
   *         enhanced.showFilterTree = true;
   *       }
   *       return enhanced;
   *     }
   *   };
   * });
   * ```
   */
  protected addShowFilterTreeParam(params: FilterAPIParams): FilterAPIParams {
    const filterState = this.store.getState();
    const filterToggleButton = (
      filterState as unknown as { 'filter-toggle-button'?: { showFilterTree?: boolean } }
    )['filter-toggle-button'];

    if (filterToggleButton && filterToggleButton.showFilterTree) {
      return { ...params, showFilterTree: true };
    }
    return params;
  }

  /**
   * Adds collection scope and tag parameters from app configuration.
   *
   * Includes collection_id and associated tags from general settings when filtering
   * within a specific collection context. Override to customize collection scoping
   * or add additional collection-based parameters.
   *
   * @param params - Current filter parameters being built
   * @returns Enhanced parameters with collection_scope and tag if collection context exists
   *
   * @remarks
   * Collection Scoping:
   * - Only adds parameters if collection_id exists in general settings
   * - Includes first tag from collection_tags array
   * - Used to scope search/filter results to specific collection
   * - Empty string tag if no collection tags configured
   *
   * @example
   * Override to add collection metadata:
   * ```javascript
   * window.boostWidgetIntegration.extend('FilterAPI', (FilterAPI) => {
   *   return class CustomFilterAPI extends FilterAPI {
   *     protected addCollectionScope(params) {
   *       const enhanced = super.addCollectionScope(params);
   *       // Add collection handle for better tracking
   *       const collectionId = this.appService.TAEAppConfig.generalSettings?.collection_id;
   *       if (collectionId) {
   *         enhanced.collection_handle = this.getCollectionHandle(collectionId);
   *         enhanced.collection_type = this.getCollectionType(collectionId);
   *       }
   *       return enhanced;
   *     }
   *   };
   * });
   * ```
   */
  protected addCollectionScope(params: FilterAPIParams): FilterAPIParams {
    const collectionId = this.appService.TAEAppConfig.generalSettings?.collection_id;

    if (collectionId !== undefined && collectionId !== null) {
      return {
        ...params,
        collection_scope: collectionId,
        tag: this.appService.TAEAppConfig.generalSettings.collection_tags?.[0] || '',
      };
    }
    return params;
  }

  /**
   * Transforms pf_tag parameter to standard tag parameter format.
   *
   * Renames the filter-specific `pf_tag` parameter to the standard `tag` parameter
   * expected by the API. This normalization ensures consistent tag handling across
   * different contexts. Override to customize tag parameter transformation.
   *
   * @param params - Current filter parameters being built
   * @returns Parameters with pf_tag renamed to tag, or unchanged if no pf_tag exists
   *
   * @remarks
   * Tag Parameter Normalization:
   * - Converts pf_tag (filter-specific format) → tag (API format)
   * - Removes pf_tag from final parameters
   * - No-op if pf_tag doesn't exist
   * - Preserves all other parameters
   *
   * @example
   * Override to handle multiple tag formats:
   * ```javascript
   * window.boostWidgetIntegration.extend('FilterAPI', (FilterAPI) => {
   *   return class CustomFilterAPI extends FilterAPI {
   *     protected handleTagParams(params) {
   *       let enhanced = super.handleTagParams(params);
   *       // Also handle custom tag formats
   *       if (enhanced.custom_tags) {
   *         enhanced.tag = this.mergeTagFormats(enhanced.tag, enhanced.custom_tags);
   *         delete enhanced.custom_tags;
   *       }
   *       return enhanced;
   *     }
   *   };
   * });
   * ```
   */
  protected handleTagParams(params: FilterAPIParams): FilterAPIParams {
    if (params.pf_tag) {
      const { pf_tag, ...rest } = params;
      return { ...rest, tag: pf_tag };
    }
    return params;
  }
  /**
   * Adds or removes filter flag based on presence of filter parameters.
   *
   * Checks if any filter parameters (those starting with 'pf_') exist and adds
   * a `filter: true` flag to indicate filters are active. If no filter params
   * exist, removes the filter flag. This helps the API optimize query execution.
   * Override to customize filter flag logic.
   *
   * @param params - Current filter parameters being built
   * @param filteredParams - Optional pre-filtered params to inspect for filter keys. Defaults to `params`
   * @returns Parameters with filter flag added if filter params exist, or with flag removed
   *
   * @remarks
   * Filter Flag Behavior:
   * - Detects filter params by checking for 'pf_' prefix (e.g., pf_st_color, pf_p_price)
   * - Adds filter: true if any filter params found
   * - Removes filter flag if no filter params (clean params)
   * - API uses this flag to determine if filter optimization should be applied
   *
   * @example
   * Override to add custom filter detection:
   * ```javascript
   * window.boostWidgetIntegration.extend('FilterAPI', (FilterAPI) => {
   *   return class CustomFilterAPI extends FilterAPI {
   *     protected addFilterFlag(params, filteredParams = params) {
   *       const enhanced = super.addFilterFlag(params, filteredParams);
   *       // Also detect custom filter formats
   *       const hasCustomFilters = Object.keys(filteredParams).some(key =>
   *         key.startsWith('custom_filter_')
   *       );
   *       if (hasCustomFilters) {
   *         enhanced.filter = true;
   *         enhanced.custom_filter_mode = true;
   *       }
   *       return enhanced;
   *     }
   *   };
   * });
   * ```
   */
  protected addFilterFlag(
    params: FilterAPIParams,
    filteredParams: FilterAPIParams = params
  ): FilterAPIParams {
    // Check if params has any filter parameters (those starting with 'pf_')
    const hasFilterParams = Object.keys(filteredParams).some((key) => key.startsWith('pf_'));

    if (hasFilterParams) {
      return { ...params, filter: true };
    }

    const { filter, ...rest } = params;
    return rest;
  }

  /**
   * Adds vendor parameter when on a vendor-specific page.
   *
   * Detects if the current page is a vendor page and extracts the vendor name
   * from the URL query parameter 'q', then includes it in the API request.
   * Override to customize vendor detection or add vendor-specific parameters.
   *
   * @param params - Current filter parameters being built
   * @returns Enhanced parameters with vendor name if on vendor page, unchanged otherwise
   *
   * @remarks
   * Vendor Page Detection:
   * - Uses FilterHelper.isVendorPage() to detect vendor pages
   * - Extracts vendor name from URL query parameter 'q'
   * - Common URL format: /pages/vendors?q=vendor-name
   * - Only adds vendor param if 'q' parameter exists
   *
   * @example
   * Override to add vendor metadata:
   * ```javascript
   * window.boostWidgetIntegration.extend('FilterAPI', (FilterAPI) => {
   *   return class CustomFilterAPI extends FilterAPI {
   *     protected handleVendorPage(params) {
   *       const enhanced = super.handleVendorPage(params);
   *       // Add vendor ID and metadata
   *       if (enhanced.vendor) {
   *         const vendorData = this.getVendorMetadata(enhanced.vendor);
   *         enhanced.vendor_id = vendorData.id;
   *         enhanced.vendor_tier = vendorData.tier; // premium, standard, etc.
   *       }
   *       return enhanced;
   *     }
   *   };
   * });
   * ```
   */
  protected handleVendorPage(params: FilterAPIParams): FilterAPIParams {
    if (this.helper.isVendorPage()) {
      const urlObj = new URL(window.location.href);
      const q = urlObj.searchParams.get('q');
      if (q) {
        return { ...params, vendor: q };
      }
    }
    return params;
  }

  /**
   * Adds banner interleaving parameter if feature is enabled.
   *
   * Checks template metadata for banner interleaving feature flag and includes
   * insert_banners parameter if enabled. This tells the API to include promotional
   * banner content in the product results. Override to customize banner insertion logic.
   *
   * @param params - Current filter parameters being built
   * @returns Enhanced parameters with insert_banners flag if feature is enabled
   *
   * @remarks
   * Banner Interleaving:
   * - Reads enableBannerInterleaving from templateMetadata.featureOptions
   * - When enabled, API interleaves promotional banners with product results
   * - Banners are configured in admin dashboard
   * - Override to control when banners should be shown
   *
   * @example
   * Override to add banner targeting:
   * ```javascript
   * window.boostWidgetIntegration.extend('FilterAPI', (FilterAPI) => {
   *   return class CustomFilterAPI extends FilterAPI {
   *     protected handleBannerParams(params) {
   *       const enhanced = super.handleBannerParams(params);
   *       // Add banner targeting based on customer segment
   *       if (enhanced.insert_banners) {
   *         const customer = this.getCustomerData();
   *         enhanced.banner_target_segment = customer?.tags?.includes('vip') ? 'vip' : 'all';
   *         enhanced.banner_position = this.getBannerPosition();
   *       }
   *       return enhanced;
   *     }
   *   };
   * });
   * ```
   */
  protected handleBannerParams(params: FilterAPIParams): FilterAPIParams {
    const enableBannerInterleaving =
      (this.appService.TAEAppConfig.templateMetadata as any).featureOptions
        ?.enableBannerInterleaving || false;
    if (enableBannerInterleaving) {
      return { ...params, insert_banners: true };
    }
    return params;
  }

  /**
   * Applies filter-specific settings to query parameters.
   *
   * This method applies shop configuration settings such as sorting behavior,
   * out-of-stock display options, and availability filtering. Override this method
   * to customize filter settings based on shop-specific requirements.
   *
   * @param queryParams - URLSearchParams to enhance with filter settings
   *
   * @example
   * Extend to add custom availability filtering:
   * ```javascript
   * window.boostWidgetIntegration.extend('FilterAPI', (FilterAPI) => {
   *   return class CustomFilterAPI extends FilterAPI {
   *     protected applyFilterSettings(queryParams) {
   *       super.applyFilterSettings(queryParams);
   *       // Always hide out of stock for VIP customers
   *       if (this.customerHasVIPStatus()) {
   *         queryParams.set('show_out_of_stock', 'false');
   *       }
   *     }
   *   };
   * });
   * ```
   */
  protected applyFilterSettings(queryParams: URLSearchParams): void {
    const settings = this.appService.filterSettings;
    const key = queryParams.toString();
    const hasFilterParams = key.includes('pf_');

    // Apply sorting available first setting
    if (settings.sortingAvailableFirst) {
      queryParams.set('sort_first', 'available');
    }

    // Apply out of stock display options
    const availableAfterFiltering = settings.availableAfterFiltering;
    const productAndVariantAvailable = settings.productAndVariantAvailable;

    if (!availableAfterFiltering && !productAndVariantAvailable) {
      // show out of stock products/variants : availableAfterFiltering:false, productAndVariantAvailable: false
      queryParams.set('product_available', 'false');
      queryParams.set('variant_available', 'false');
    } else if (!availableAfterFiltering && productAndVariantAvailable) {
      //hide out of stock products/variants: availableAfterFiltering:false, productAndVariantAvailable:true
      queryParams.set('product_available', 'true');
      queryParams.set('variant_available', 'true');
    } else {
      //only hide out of stock products/variants after filtering: availableAfterFiltering: true  productAndVariantAvailable:false
      queryParams.set('product_available', `${hasFilterParams}`);
      queryParams.set('variant_available', `${hasFilterParams}`);
    }

    // Apply zero_options flag when showOutOfStockOption is enabled
    if (settings.showOutOfStockOption) {
      queryParams.set('zero_options', 'true');
    }
  }

  /**
   * Adds custom parameters from global configuration.
   *
   * This method reads custom parameters from `window.boostSdCustomParams` or
   * `window.boostWidgetIntegration.config.customization.filter.customizedAPIParams`
   * and adds them to the API request. Override this method to add shop-specific
   * custom parameters or modify parameter handling logic.
   *
   * @param queryParams - URLSearchParams to enhance with custom parameters
   *
   * @example
   * Extend to add shop-specific tracking parameters:
   * ```javascript
   * window.boostWidgetIntegration.extend('FilterAPI', (FilterAPI) => {
   *   return class CustomFilterAPI extends FilterAPI {
   *     protected addCustomParams(queryParams) {
   *       super.addCustomParams(queryParams);
   *       // Add shop-specific tracking for analytics
   *       queryParams.set('shop_id', this.getShopId());
   *       queryParams.set('utm_source', 'boost_filter');
   *     }
   *   };
   * });
   * ```
   */
  protected addCustomParams(queryParams: URLSearchParams): void {
    let customParams: Record<string, unknown> | undefined;

    const firstParams = (
      window as unknown as {
        boostSdCustomParams?: Record<string, unknown>;
      }
    ).boostSdCustomParams;

    if (
      firstParams &&
      typeof firstParams === 'object' &&
      firstParams !== null &&
      Object.keys(firstParams).length > 0
    ) {
      customParams = firstParams;
    } else {
      customParams = (
        window as unknown as {
          boostWidgetIntegration?: {
            config?: {
              customization?: { filter?: { customizedAPIParams?: Record<string, unknown> } };
            };
          };
        }
      ).boostWidgetIntegration?.config?.customization?.filter?.customizedAPIParams;
    }

    if (customParams && typeof customParams === 'object' && customParams !== null) {
      Object.entries(customParams).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          queryParams.set(key, String(value));
        }
      });
    }
  }

  /**
   * Makes the actual API request to fetch filter data.
   *
   * This method handles both template-based rendering (when widgetId is provided)
   * and standard JSON/HTML responses. It performs the HTTP request, processes
   * the response data, and handles currency metadata. Override this method to
   * customize request behavior such as adding authentication, custom headers,
   * or implementing retry logic.
   *
   * @param url - API endpoint URL to fetch filter data from
   * @param queryParams - Query parameters to include in the request
   * @param responseType - Expected response type ('json' or 'html')
   * @returns Promise resolving to API response data, or null if request fails
   *
   * @example
   * Extend to add custom authentication headers:
   * ```javascript
   * window.boostWidgetIntegration.extend('FilterAPI', (FilterAPI) => {
   *   return class CustomFilterAPI extends FilterAPI {
   *     protected async makeAPIRequest(url, queryParams, responseType) {
   *       // Add shop-specific authentication token
   *       const widgetId = queryParams.get('widgetId');
   *       const template = widgetId ? this.getTemplateParsedById(widgetId) : null;
   *
   *       if (template && typeof template === 'string') {
   *         queryParams.delete('widgetId');
   *         const response = await fetch(`${url}?${queryParams.toString()}`, {
   *           method: 'GET',
   *           headers: {
   *             'X-Shop-Token': this.getShopAuthToken()
   *           }
   *         });
   *         // Handle response...
   *       }
   *
   *       return super.makeAPIRequest(url, queryParams, responseType);
   *     }
   *   };
   * });
   * ```
   */
  protected async makeAPIRequest(
    url: string,
    queryParams: URLSearchParams,
    responseType: 'json' | 'html'
  ): Promise<FilterAPIResponse | null> {
    try {
      // Check if widgetId exists for template-based rendering
      const widgetId = queryParams.get('widgetId');
      const template = widgetId ? this.getTemplateParsedById(widgetId) : null;

      if (template && typeof template === 'string') {
        // Template-based rendering flow
        queryParams.delete('widgetId');

        const response = await fetch(`${url}?${queryParams.toString()}`, {
          method: 'GET',
        });

        if (!response.ok) {
          throw new Error(`API request failed: ${response.statusText}`);
        }

        const data = await response.json();

        // Convert queryParams to object
        const queryObject: Record<string, string> = {};
        for (const [key, value] of queryParams.entries()) {
          queryObject[key] = value;
        }

        // Normalize query object into params
        const params = this.helper.normalizeObject(queryObject);

        // Render template with response data
        const html = await this.appService.templateRender(template, {
          ...data,
          request: { ...queryObject, ...params },
        });

        // Handle currency metadata if present
        if (data.meta) {
          this.handleCurrencyMetadata(data.meta);
        }

        return {
          html,
          filter: data.filter || { filterId: '', options: [] },
          bundles: data.bundles || [],
          request_id: data.meta?.rid,
          meta: data.meta,
        };
      } else {
        // Standard request flow
        const response = await fetch(`${url}?${queryParams.toString()}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          throw new Error(`API request failed: ${response.statusText}`);
        }

        if (responseType === 'json') {
          const data = await response.json();

          // Handle currency metadata if present
          if (data.meta) {
            this.handleCurrencyMetadata(data.meta);
          }

          return {
            html: data.html || '',
            filter: data.filter || { filterId: '', options: [] },
            bundles: data.bundles || [],
            request_id: data.request_id,
            meta: data.meta,
          };
        } else {
          const html = await response.text();
          return {
            html,
            filter: { filterId: '', options: [] },
            bundles: [],
          };
        }
      }
    } catch (error) {
      console.error('FilterAPI request error:', error);
      throw error;
    }
  }

  /**
   * Gets template parsed by ID from app configuration.
   *
   * This method resolves a template by its ID from the TAE app configuration,
   * checking both `templateParsed` and `template` properties. Override this method
   * to customize template resolution logic or provide custom templates for specific shops.
   *
   * @param templateId - The template ID to look up in configuration
   * @returns The parsed template object or null if not found
   *
   * @example
   * Extend to provide custom templates:
   * ```javascript
   * window.boostWidgetIntegration.extend('FilterAPI', (FilterAPI) => {
   *   return class CustomFilterAPI extends FilterAPI {
   *     protected getTemplateParsedById(templateId) {
   *       // Use custom template for specific shops
   *       if (templateId === 'custom-shop-template') {
   *         return this.getCustomShopTemplate();
   *       }
   *       return super.getTemplateParsedById(templateId);
   *     }
   *   };
   * });
   * ```
   */
  protected getTemplateParsedById(templateId: string): unknown | null {
    const templateMetadata = this.appService.TAEAppConfig.templateMetadata as
      | Record<string, string>
      | undefined;
    if (!templateMetadata) return null;

    let templateName: string | null = null;
    for (const key in templateMetadata) {
      if (Object.prototype.hasOwnProperty.call(templateMetadata, key)) {
        if (templateMetadata[key] === templateId) {
          templateName = key;
          break;
        }
      }
    }

    if (!templateName) return null;

    const templateParsed = (
      this.appService.TAEAppConfig as { templateParsed?: Record<string, unknown> }
    ).templateParsed;
    if (templateParsed && templateParsed[templateName]) {
      return templateParsed[templateName];
    }

    const templates = (this.appService.TAEAppConfig as { template?: Record<string, unknown> })
      .template;
    if (templates && templates[templateName]) {
      return templates[templateName];
    }

    return null;
  }

  /**
   * Handles currency metadata from API response.
   *
   * This method updates shop currency settings (money_format, money_format_with_currency, currency)
   * based on metadata returned from the API. Override this method to customize currency
   * handling or apply shop-specific currency transformations.
   *
   * @param meta - Metadata from API response containing currency information
   *
   * @example
   * Extend to add custom currency formatting:
   * ```javascript
   * window.boostWidgetIntegration.extend('FilterAPI', (FilterAPI) => {
   *   return class CustomFilterAPI extends FilterAPI {
   *     protected handleCurrencyMetadata(meta) {
   *       super.handleCurrencyMetadata(meta);
   *       // Apply shop-specific currency rounding rules
   *       if (this.appService.TAEAppConfig.shop) {
   *         this.appService.TAEAppConfig.shop.round_prices = true;
   *       }
   *     }
   *   };
   * });
   * ```
   */
  protected handleCurrencyMetadata(meta: {
    money_format?: string;
    money_format_with_currency?: string;
    currency?: string;
  }): void {
    const shop = (this.appService.TAEAppConfig as { shop?: Record<string, unknown> }).shop;
    if (!shop) return;

    if (meta.money_format) {
      shop.money_format = meta.money_format;
    }

    if (meta.money_format_with_currency) {
      shop.money_format_with_currency = meta.money_format_with_currency;
    }

    if (meta.currency) {
      shop.currency = meta.currency;
    }
  }

  /**
   * Protected method for building query parameters - can be overridden for customization
   *
   * @param params - Base parameters
   * @returns Enhanced parameters
   *
   * @example
   * Override to add custom parameters:
   * ```typescript
   * class CustomFilterAPI extends FilterAPI {
   *   protected buildFilterQueryParams(params: FilterAPIParams): FilterAPIParams {
   *     const enhanced = super.buildFilterQueryParams(params);
   *     enhanced.custom_param = 'custom_value';
   *     return enhanced;
   *   }
   * }
   * ```
   */
  protected buildFilterQueryParams(params: FilterAPIParams): FilterAPIParams {
    return params;
  }

  /**
   * Gets the latest request cache key
   *
   * @returns The latest request key or null
   */
  getLatestRequestKey(): string | null {
    return this.store.getState().latestRequestKey;
  }

  /**
   * Clears all cached data
   */
  clearCache(): void {
    this.store.clearCache();
  }
}
